import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import {
  getSettings,
  saveSettings,
  subtypeLabels,
  type QuestionSettings,
  type CircuitSettings,
} from "@/lib/settings";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  questionType: keyof CircuitSettings;
}

export function QuestionSettingsDialog({ isOpen, onClose, questionType }: Props) {
  const [settings, setSettings] = useState<QuestionSettings | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSettings(getSettings());
    }
  }, [isOpen]);

  const handleSubtypeToggle = (subtype: string) => {
    if (!settings) return;
    setSettings((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        circuits: {
          ...prev.circuits,
          [questionType]: {
            ...prev.circuits[questionType],
            subtypes: {
              ...prev.circuits[questionType].subtypes,
              [subtype]: !prev.circuits[questionType].subtypes[subtype],
            },
          },
        },
      };
    });
  };

  const handleSave = () => {
    if (settings) {
      saveSettings(settings);
    }
    onClose();
  };

  const subtypes = subtypeLabels[questionType] || {};
  const currentSubtypes = settings?.circuits[questionType]?.subtypes || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-xs"
          >
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 mx-4">
              {/* Header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-medium text-gray-900">Question Variants</h3>
              </div>

              {/* Checkboxes */}
              <div className="p-2">
                {Object.entries(subtypes).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      checked={currentSubtypes[key] ?? true}
                      onChange={() => handleSubtypeToggle(key)}
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        onChange();
      }}
      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
        checked
          ? "bg-gray-900 border-gray-900"
          : "bg-white border-gray-300 hover:border-gray-400"
      }`}
    >
      {checked && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
    </button>
  );
}
